import './OperatorDeposits.scss';
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import Pagination from '../../components/Layout/Pagination';
import EmptyState from '../../components/Layout/EmptyState';
import Toast from '../../components/Toast/Toast';
import { getApiErrorMessage } from '../../utils/apiErrors';

export default function OperatorDeposits() {
    const { t } = useTranslation('layout');
    const api_url = useSelector((state) => state.API_URL);

    const [deposits, setDeposits] = useState([]);
    const [traders, setTraders] = useState([]);
    const [teamleadBalance, setTeamleadBalance] = useState(0);
    const [loading, setLoading] = useState(true);

    const [selectedTrader, setSelectedTrader] = useState('');
    const [amount, setAmount] = useState('');
    const [isReverse, setIsReverse] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const fetchData = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        if (!jwtToken) return;
        try {
            const response = await fetch(api_url + 'teamlead/deposits/', {
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
            });
            if (response.ok) {
                const data = await response.json();
                setDeposits(data.deposits || []);
                setTraders(data.traders || []);
                setTeamleadBalance(data.teamlead_balance || 0);
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [api_url]);

    useProtectedInterval(fetchData, [api_url]);

    const handleSubmit = async () => {
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt <= 0) {
            setToast({ type: 'error', message: 'Укажите корректную сумму' });
            return;
        }
        if (!selectedTrader) {
            setToast({ type: 'error', message: 'Выберите трейдера' });
            return;
        }

        setSubmitting(true);
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + 'teamlead/deposits/', {
                method: 'POST',
                headers: { 'Authorization': `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: selectedTrader, amount: amt, is_reverse: isReverse }),
            });
            const data = await response.json().catch(() => null);
            if (response.ok) {
                setToast({ type: 'success', message: data.message });
                setAmount('');
                fetchData();
            } else {
                setToast({ type: 'error', message: getApiErrorMessage(data) });
            }
        } catch (e) {
            setToast({ type: 'error', message: 'Ошибка сети' });
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleDateString('ru-RU') + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    };

    const paged = deposits.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    if (loading) return <div style={{ padding: '24px' }}><h2>{t('sidebar.menu.deposits')}</h2><p>Загрузка...</p></div>;

    return (
        <div className="Deposits">
            {toast && <Toast show type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
            <h2>{t('sidebar.menu.deposits')}</h2>

            {/* Балансы трейдеров */}
            <div className="Deposits__balances">
                {traders.map(tr => (
                    <div key={tr.id} className={`Deposits__trader-card${tr.balance_usdt < 0 ? ' Deposits__trader-card--negative' : ''}`}>
                        <span className="Deposits__trader-name">{tr.username}</span>
                        <span className="Deposits__trader-balance">{tr.balance_usdt} $</span>
                    </div>
                ))}
            </div>

            {/* Форма */}
            <div className="Deposits__form-card">
                <div className="Deposits__form-info">
                    <span>Ваш баланс: <b>{teamleadBalance} $</b></span>
                </div>

                <div className="Deposits__form-row">
                    <select
                        className="Deposits__select"
                        value={selectedTrader}
                        onChange={(e) => setSelectedTrader(e.target.value)}
                        disabled={submitting}
                    >
                        <option value="">Выберите трейдера</option>
                        {traders.map(tr => (
                            <option key={tr.id} value={tr.id}>{tr.username} ({tr.balance_usdt} $)</option>
                        ))}
                    </select>

                    <input
                        className="Deposits__input"
                        type="number"
                        placeholder="Сумма USDT"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        disabled={submitting}
                        min="0"
                        step="1"
                    />

                    <div className="Deposits__direction">
                        <label className={`Deposits__dir-btn${!isReverse ? ' Deposits__dir-btn--active' : ''}`}>
                            <input type="radio" checked={!isReverse} onChange={() => setIsReverse(false)} />
                            Начислить
                        </label>
                        <label className={`Deposits__dir-btn${isReverse ? ' Deposits__dir-btn--active Deposits__dir-btn--reverse' : ''}`}>
                            <input type="radio" checked={isReverse} onChange={() => setIsReverse(true)} />
                            Списать
                        </label>
                    </div>

                    <button
                        className="Deposits__submit"
                        onClick={handleSubmit}
                        disabled={submitting || !selectedTrader || !amount}
                    >
                        {submitting ? '...' : isReverse ? 'Списать' : 'Начислить'}
                    </button>
                </div>
            </div>

            {/* История */}
            <h3 className="Deposits__subtitle">История операций (21 день)</h3>

            <Pagination pageIndex={pageIndex} totalCount={deposits.length} pageSize={pageSize}
                onPageChange={setPageIndex} onPageSizeChange={setPageSize} pageSizes={[5, 10, 20]} />

            {paged.length === 0 ? (
                <EmptyState message="Нет операций за последние 21 день" />
            ) : (
                <div className="Deposits__table-wrap">
                    <table className="Deposits__table">
                        <thead>
                            <tr>
                                <th>Трейдер</th>
                                <th>Сумма</th>
                                <th>Тип</th>
                                <th>Комментарий</th>
                                <th>Дата</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.map(d => (
                                <tr key={d.id}>
                                    <td className="Deposits__username">{d.user}</td>
                                    <td className={`Deposits__amount${d.is_reverse ? ' Deposits__amount--reverse' : ' Deposits__amount--deposit'}`}>
                                        {d.is_reverse ? '−' : '+'}{d.amount} {d.kind}
                                    </td>
                                    <td>{d.is_reverse ? 'Списание' : 'Начисление'}</td>
                                    <td className="Deposits__comment">{d.comment || '—'}</td>
                                    <td className="Deposits__date">{formatDate(d.created)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination pageIndex={pageIndex} totalCount={deposits.length} pageSize={pageSize}
                onPageChange={setPageIndex} onPageSizeChange={setPageSize} pageSizes={[5, 10, 20]} />
        </div>
    );
}
